import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import Typography from "@mui/material/Typography";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  AdditionalInsuredInput,
  Relationship,
} from "@covertree/new-business-graphql";
import { useFormWithNotification } from "@covertree/hooks";
import { FORM_ERROR_DELAY_IN_MS } from "@covertree/consts";
import {
  additionalInsuredFormSchema,
  relationshipOptions,
} from "@covertree/new-business-forms";
import {
  Button,
  Form,
  FormSelectField,
  FormTextField,
  FormToggleCard,
} from "@covertree/ui";
import { theme } from "@covertree/theme";

interface AddSomeoneElseInlineProps {
  firstPersonName?: string;
  additionalInsured?: AdditionalInsuredInput | null;
  subheader?: string;
  disabled?: boolean;
  onSubmit: (data: AdditionalInsuredInput) => void;
  onRemove: () => void;
}

/**
 * Endorsement-local "Add someone else to the policy" control — like the shared
 * `FormAddSomeoneElseToggle`, but reveals the person form inline instead of in a
 * modal.
 */
export const AddSomeoneElseInline = ({
  firstPersonName,
  additionalInsured,
  subheader,
  disabled = false,
  onSubmit,
  onRemove,
}: AddSomeoneElseInlineProps) => {
  const form = useFormWithNotification<AdditionalInsuredInput>({
    mode: "all",
    defaultValues: {
      firstName: "",
      lastName: "",
      relationship: "" as unknown as Relationship,
    },
    delayError: FORM_ERROR_DELAY_IN_MS,
    reValidateMode: "onSubmit",
    // Resolver/value types don't line up cleanly, as in the shared component.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: yupResolver(additionalInsuredFormSchema),
  });

  const { control, handleSubmit, reset } = form;

  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleToggle = () => {
    if (additionalInsured) {
      onRemove();
      setIsFormOpen(false);

      return;
    }

    setIsFormOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isFormOpen) reset();
  }, [isFormOpen, reset]);

  const submit = (data: AdditionalInsuredInput) => {
    onSubmit(data);
    setIsFormOpen(false);
  };

  const showInlineForm = isFormOpen && !additionalInsured;

  return (
    <Box
      sx={{
        "& .MuiCardContent-root": { paddingBottom: 0 },
      }}>
      <FormToggleCard
        title="Add someone else to the policy"
        checked={!!additionalInsured || isFormOpen}
        checkedCustomStyles={{
          border: `1px solid ${theme.palette.grey[300]}`,
        }}
        onToggle={handleToggle}
        fontSize={16}
        tooltip="You can add a spouse or a second owner to the policy"
        actionText="Free"
        subheader={
          <Typography color={theme.palette.grey[600]} fontSize={16}>
            {subheader}
          </Typography>
        }
        disabled={disabled}
        content={
          showInlineForm ? (
            <FormProvider {...form}>
              <Form onSubmit={handleSubmit(submit)}>
                <Grid
                  container
                  spacing={1.5}
                  component={Box}
                  overflow="hidden"
                  pt={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" fontSize={18}>
                      Secondary person in the policy
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      id="secondary-person-first-name"
                      dataTestId="secondary-person-first-name"
                      name="firstName"
                      label="First name"
                      control={control}
                      fontSize={18}
                      labelFontSize={16}
                      sx={{ backgroundColor: "background.default" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      id="secondary-person-last-name"
                      dataTestId="secondary-person-last-name"
                      name="lastName"
                      label="Last name"
                      control={control}
                      fontSize={18}
                      labelFontSize={16}
                      sx={{ backgroundColor: "background.default" }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormSelectField
                      id="relationship"
                      name="relationship"
                      dataTestId="relationship-dropdown"
                      label={
                        firstPersonName
                          ? `Relationship to ${firstPersonName}`
                          : "Relationship"
                      }
                      options={relationshipOptions}
                      placeholder="Select..."
                      control={control}
                      fontSize={18}
                      labelFontSize={16}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    mt={2}
                    display="flex"
                    justifyContent="flex-end">
                    <Button
                      type="submit"
                      size="small"
                      data-testid="add-person-button">
                      Add person
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            </FormProvider>
          ) : undefined
        }
      />
    </Box>
  );
};
